import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  ViewContainerRef,
} from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { fromEvent, Subscription } from 'rxjs';
import { MdePopoverComponent } from './mde-popover.component';

@Directive({
  selector: '[mdePopoverTriggerFor]',
  exportAs: 'mdePopoverTrigger',
  standalone: false,
})
export class MdePopoverTriggerDirective implements AfterViewInit, OnDestroy {
  @Input('mdePopoverTriggerFor') popover!: MdePopoverComponent;
  @Input() mdePopoverTriggerOn: 'click' | 'hover' = 'click';

  private overlayRef: OverlayRef | null = null;
  private triggerSubs = new Subscription();
  private panelSubs = new Subscription();
  private _isOpen = false;
  private hoverTimer: any;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
  ) {}

  ngAfterViewInit(): void {
    const el = this.elementRef.nativeElement;
    if (this.mdePopoverTriggerOn === 'click') {
      this.triggerSubs.add(
        fromEvent(el, 'click').subscribe(() => this.togglePopover())
      );
    } else {
      this.triggerSubs.add(
        fromEvent(el, 'mouseenter').subscribe(() => {
          clearTimeout(this.hoverTimer);
          this.openPopover();
        })
      );
      this.triggerSubs.add(
        fromEvent(el, 'mouseleave').subscribe(() => {
          this.hoverTimer = setTimeout(() => this.closePopover(), 200);
        })
      );
    }
  }

  togglePopover(): void {
    this._isOpen ? this.closePopover() : this.openPopover();
  }

  openPopover(): void {
    if (this._isOpen || !this.popover?.templateRef) return;
    this._isOpen = true;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions(this.getPositions())
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: this.mdePopoverTriggerOn === 'click',
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    const portal = new TemplatePortal(this.popover.templateRef, this.viewContainerRef);
    this.overlayRef.attach(portal);

    if (this.mdePopoverTriggerOn === 'click') {
      this.panelSubs.add(
        this.overlayRef.backdropClick().subscribe(() => this.closePopover())
      );
    }

    if (this.popover.mdePopoverCloseOnClick) {
      this.panelSubs.add(
        fromEvent<MouseEvent>(this.overlayRef.overlayElement, 'click').subscribe(() =>
          this.closePopover()
        )
      );
    }

    if (this.mdePopoverTriggerOn === 'hover') {
      this.panelSubs.add(
        fromEvent(this.overlayRef.overlayElement, 'mouseenter').subscribe(() =>
          clearTimeout(this.hoverTimer)
        )
      );
      this.panelSubs.add(
        fromEvent(this.overlayRef.overlayElement, 'mouseleave').subscribe(() => {
          this.hoverTimer = setTimeout(() => this.closePopover(), 200);
        })
      );
    }
  }

  closePopover(): void {
    if (!this._isOpen) return;
    this._isOpen = false;
    this.panelSubs.unsubscribe();
    this.panelSubs = new Subscription();
    this.overlayRef?.detach();
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  private getPositions(): ConnectedPosition[] {
    if (this.popover?.mdePopoverOverlapTrigger) {
      return [
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' },
      ];
    }
    return [
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
      { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
      { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
      { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
    ];
  }

  ngOnDestroy(): void {
    clearTimeout(this.hoverTimer);
    this.closePopover();
    this.triggerSubs.unsubscribe();
  }
}
